import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BuildingIcon, ChevronsUpDownIcon, ClipboardListIcon, KanbanSquareIcon, LayoutDashboardIcon, LayoutTemplateIcon, LogOutIcon, MenuIcon, PlugIcon, SettingsIcon, Trash2Icon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrandTheme } from '../hooks/useBrandTheme';
import { Permission } from '../config/permissions';
import { ROLE_LABELS } from '../types';
import { BrandLogo } from '../components/shared/BrandLogo';
import { Avatar } from '../components/ui/Avatar';
import { Dropdown } from '../components/ui/Dropdown';
import { Drawer } from '../components/ui/Drawer';
interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  permission?: Permission;
}
const NAV: NavItem[] = [{
  to: '/app/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboardIcon
}, {
  to: '/app/leads',
  label: 'Leads',
  icon: UsersIcon
}, {
  to: '/app/pipeline',
  label: 'Embudo',
  icon: KanbanSquareIcon
}, {
  to: '/app/formularios',
  label: 'Formularios',
  icon: ClipboardListIcon,
  permission: 'forms.manage'
}, {
  to: '/app/landings',
  label: 'Landings',
  icon: LayoutTemplateIcon,
  permission: 'landings.manage'
}, {
  to: '/app/papelera',
  label: 'Papelera',
  icon: Trash2Icon
}, {
  to: '/app/equipo',
  label: 'Equipo',
  icon: UsersRoundIcon,
  permission: 'team.manage'
}, {
  to: '/app/integraciones',
  label: 'Integraciones',
  icon: PlugIcon,
  permission: 'integrations.manage'
}, {
  to: '/app/configuracion',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings.manage'
}];
function navClass({
  isActive
}: {
  isActive: boolean;
}): string {
  return ['flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]', isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface hover:text-foreground'].join(' ');
}
export function AppLayout() {
  const {
    organization,
    user,
    role,
    hasPermission,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // La identidad visual de la empresa se aplica a toda la plataforma.
  useBrandTheme(organization);
  const items = NAV.filter(item => !item.permission || hasPermission(item.permission));
  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const nav = <nav className="flex flex-col gap-1" aria-label="Navegación principal" data-id="element-688" data-mp="35fnu64odri9b">
      {items.map(item => <NavLink key={item.to} to={item.to} className={navClass} onClick={() => setMenuOpen(false)} data-id="element-689" data-mp="30gb5rfhv234m">
          <item.icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-690" data-mp="2vij6u1istgqx" />
          {item.label}
        </NavLink>)}
    </nav>;
  const workspaceCard = <div className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5" data-id="element-691" data-mp="2qhvftfukkzwp">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-692" data-mp="2mhv0lsznckol">
        {organization?.brand.logoUrl ? <img src={organization.brand.logoUrl} alt="" className="h-6 w-6 object-contain" data-id="element-693" data-mp="160ci458yjr7k" /> : <BuildingIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-694" data-mp="162gy3lk4sgo7" />}
      </span>
      <span className="min-w-0 flex-1" data-id="element-695" data-mp="1axmey5yof55f">
        <span className="block truncate text-sm font-semibold text-foreground" data-id="element-696" data-mp="1fzbqrntq3muk">
          {organization?.name ?? 'Mi empresa'}
        </span>
        <span className="block truncate text-xs text-muted-foreground" data-id="element-697" data-mp="icuzp0bl25h9">
          {organization?.sector}
        </span>
      </span>
      <ChevronsUpDownIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-698" data-mp="s97szx37uhbh" />
    </div>;
  return <div className="flex min-h-screen w-full bg-surface" data-id="element-699" data-mp="x0g0uoowdumj">
      {/* Sidebar escritorio */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-none flex-col border-r border-border bg-background p-4 lg:flex" data-id="element-700" data-mp="3vq7rq7b89fmk">
        <div className="mb-6 px-1" data-id="element-701" data-mp="4ilj009nimyu">
          <BrandLogo src={organization?.brand.logoUrl ?? organization?.logoUrl} size={26} alt={organization?.name ?? 'Sistema AVA'} data-id="element-702" data-mp="4hk6drczlsf0" />
        </div>
        <div className="mb-4" data-id="element-703" data-mp="227qy71pbz5px">{workspaceCard}</div>
        {nav}
        <div className="mt-auto pt-4" data-id="element-704" data-mp="225eae7ww614y">
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground" data-id="element-705" data-mp="2fyvqfiqdcvq8">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-706" data-mp="2pvh22mww9531" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Menú móvil */}
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title="Menú" data-id="element-707" data-mp="2yxzzgrhwuphw">
        <div className="space-y-4" data-id="element-708" data-mp="2tyn39g55g62n">
          {workspaceCard}
          {nav}
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-surface" data-id="element-709" data-mp="2twkfl2rgyp13">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-710" data-mp="zm6xshk6gvyu" />
            Cerrar sesión
          </button>
        </div>
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-711" data-mp="3g6j0jzhcm4uj">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-712" data-mp="3g60wigog2mc2">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Abrir menú" className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-surface lg:hidden" data-id="element-713" data-mp="36gr6d6d5vdql">
            <MenuIcon className="h-5 w-5" data-id="element-714" data-mp="2xguaajofzcli" />
          </button>
          <div className="min-w-0 flex-1" data-id="element-715" data-mp="2xe83wndaf34v">
            <p className="truncate text-sm font-semibold text-foreground" data-id="element-716" data-mp="2jhbe8p8wnrpk">
              {organization?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-id="element-717" data-mp="2niwpy79pdzy6">
              {role ? ROLE_LABELS[role] : ''}
            </p>
          </div>
          <Dropdown align="right" trigger={<span className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface" data-id="element-719" data-mp="lr1ritcgjrxw">
                <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} size="sm" data-id="element-720" data-mp="1unyu89whphqf" />
                <span className="hidden text-sm font-medium text-foreground sm:inline" data-id="element-721" data-mp="1po40lylbye1o">
                  {user?.firstName}
                </span>
              </span>} items={[{
          label: 'Configuración de empresa',
          icon: <SettingsIcon className="h-4 w-4" data-id="element-722" data-mp="1gp8vyqxxpc0h" />,
          onSelect: () => navigate('/app/configuracion')
        }, {
          label: 'Cerrar sesión',
          icon: <LogOutIcon className="h-4 w-4" data-id="element-723" data-mp="29cv0q2iwemox" />,
          destructive: true,
          onSelect: handleLogout
        }]} data-id="element-718" data-mp="lqry0vs7f2ng" />
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10" data-id="element-724" data-mp="24kldiu9640qc">
          <Outlet data-id="element-725" data-mp="1znbfwrbmkirf" />
        </main>
      </div>
    </div>;
}